import React, { useEffect, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Menu, X } from "lucide-react";

const NAV_KEYS = ["home", "about", "portfolio", "market", "philosophy", "contact"];
const SECTION_IDS = {
  home: "home",
  about: "about",
  portfolio: "portfolio",
  market: "market",
  philosophy: "philosophy",
  contact: "contact",
};

export const Navbar = () => {
  const { lang, t, toggleLang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (key) => {
    setOpen(false);
    const el = document.getElementById(SECTION_IDS[key]);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="logo-button"
          onClick={() => handleNav("home")}
          className="flex items-center gap-3 group"
          aria-label="Brokers Lounge — Home"
        >
          <img
            src="/brand/brokers-lounge-logo-transparent.png"
            alt="Brokers Lounge"
            className="h-12 md:h-14 w-auto object-contain opacity-80 transition-opacity group-hover:opacity-100"
            loading="eager"
          />
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_KEYS.map((k) => (
            <button
              key={k}
              data-testid={`nav-${k}`}
              onClick={() => handleNav(k)}
              className="text-sm tracking-wide text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
            >
              {t.nav[k]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid={`lang-switch-${lang === "en" ? "ar" : "en"}`}
            onClick={toggleLang}
            className="hidden sm:inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#A1A1AA] hover:text-[#D4AF37] transition-colors border border-white/10 px-3 py-2"
            aria-label="Toggle language"
          >
            <span className={lang === "en" ? "text-[#D4AF37]" : ""}>EN</span>
            <span className="opacity-30">/</span>
            <span className={lang === "ar" ? "text-[#D4AF37]" : ""}>عربي</span>
          </button>
          <button
            data-testid="nav-cta"
            onClick={() => handleNav("contact")}
            className="hidden sm:inline-flex bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#E6C657] transition-colors duration-300 px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-medium"
          >
            {t.nav.cta}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-[#F8F8F8]"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/5"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
            {NAV_KEYS.map((k) => (
              <button
                key={k}
                data-testid={`mobile-nav-${k}`}
                onClick={() => handleNav(k)}
                className="text-start text-[#F8F8F8] hover:text-[#D4AF37] text-base"
              >
                {t.nav[k]}
              </button>
            ))}
            <button
              data-testid="mobile-lang-switch"
              onClick={toggleLang}
              className="self-start text-xs tracking-[0.2em] uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-4 py-2"
            >
              {lang === "en" ? "عربي" : "EN"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
